import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserIdleService } from 'angular-user-idle';
import { isValidPhoneNumber, parsePhoneNumber, CountryCode } from 'libphonenumber-js';
import { Subscription, interval, take } from 'rxjs';

import { AuthService } from '../auth.service';

interface Country {
  code: CountryCode;
  name: string;
  dialCode: string;
  flag: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  public loginFormGroup: FormGroup | null = null;
  public hidePassword = true;

  // OTP login state
  public phoneFormGroup: FormGroup;
  public otpFormGroup: FormGroup;
  public otpStep: 'phone' | 'otp' = 'phone';
  public isOtpLoading = false;
  public otpError: string | null = null;
  public otpCountdown = 0;
  public phoneInputFocused = false;
  public otpInputFocused = false;
  private countdownSub: Subscription | null = null;

  // Country picker
  public selectedCountryCode: CountryCode = 'HR';
  public countryDropdownOpen = false;
  public countries: Country[] = [
    { code: 'AL', name: 'Albanija', dialCode: '+355', flag: '' },
    { code: 'AT', name: 'Austrija', dialCode: '+43', flag: '' },
    { code: 'BE', name: 'Belgija', dialCode: '+32', flag: '' },
    { code: 'BA', name: 'Bosna i Hercegovina', dialCode: '+387', flag: '' },
    { code: 'BG', name: 'Bugarska', dialCode: '+359', flag: '' },
    { code: 'ME', name: 'Crna Gora', dialCode: '+382', flag: '' },
    { code: 'CZ', name: 'Republika Ceska', dialCode: '+420', flag: '' },
    { code: 'DK', name: 'Danska', dialCode: '+45', flag: '' },
    { code: 'FI', name: 'Finska', dialCode: '+358', flag: '' },
    { code: 'FR', name: 'Francuska', dialCode: '+33', flag: '' },
    { code: 'GR', name: 'Grcka', dialCode: '+30', flag: '' },
    { code: 'HR', name: 'Hrvatska', dialCode: '+385', flag: '' },
    { code: 'IE', name: 'Irska', dialCode: '+353', flag: '' },
    { code: 'IT', name: 'Italija', dialCode: '+39', flag: '' },
    { code: 'XK', name: 'Kosovo', dialCode: '+383', flag: '' },
    { code: 'HU', name: 'Madarska', dialCode: '+36', flag: '' },
    { code: 'NL', name: 'Nizozemska', dialCode: '+31', flag: '' },
    { code: 'NO', name: 'Norveska', dialCode: '+47', flag: '' },
    { code: 'DE', name: 'Njemacka', dialCode: '+49', flag: '' },
    { code: 'PL', name: 'Poljska', dialCode: '+48', flag: '' },
    { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '' },
    { code: 'RO', name: 'Rumunjska', dialCode: '+40', flag: '' },
    { code: 'MK', name: 'Sjeverna Makedonija', dialCode: '+389', flag: '' },
    { code: 'SK', name: 'Slovacka', dialCode: '+421', flag: '' },
    { code: 'SI', name: 'Slovenija', dialCode: '+386', flag: '' },
    { code: 'RS', name: 'Srbija', dialCode: '+381', flag: '' },
    { code: 'ES', name: 'Spanjolska', dialCode: '+34', flag: '' },
    { code: 'CH', name: 'Svicarska', dialCode: '+41', flag: '' },
    { code: 'SE', name: 'Svedska', dialCode: '+46', flag: '' },
    { code: 'GB', name: 'Ujedinjeno Kraljevstvo', dialCode: '+44', flag: '' },
  ];

  get selectedDialCode(): string {
    const country = this.countries.find(c => c.code === this.selectedCountryCode);
    return country ? country.dialCode : '+385';
  }

  selectCountry(country: Country): void {
    this.selectedCountryCode = country.code;
    this.countryDropdownOpen = false;
  }

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private userIdleService: UserIdleService
  ) {
    this.loginFormGroup = this.formBuilder.group({
      email: [null, Validators.required],
      password: [null, Validators.required],
    });

    this.phoneFormGroup = this.formBuilder.group({
      phoneNumber: [null, [Validators.required]],
    });

    this.otpFormGroup = this.formBuilder.group({
      otpCode: [null, [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
    });
  }

  ngOnInit(): void {
    this.userIdleService.startWatching();
    this.userIdleService.onTimerStart().subscribe();
    this.userIdleService.onTimeout().subscribe((): void => {
      if (this.authService.isLoggedIn()) {
        this.authService.logout();
      }
    });
  }

  ngOnDestroy(): void {
    this.countdownSub?.unsubscribe();
  }

  public signIn(): void {
    if (this.loginFormGroup?.valid) {
      this.authService.login({
        email: this.loginFormGroup.value.email,
        password: this.loginFormGroup.value.password,
      });
    }
  }

  public requestOtp(): void {
    const rawPhone = this.phoneFormGroup.value.phoneNumber?.trim();
    if (!rawPhone) return;

    if (!isValidPhoneNumber(rawPhone, this.selectedCountryCode)) {
      this.otpError = 'Please enter a valid phone number';
      return;
    }

    const parsed = parsePhoneNumber(rawPhone, this.selectedCountryCode);
    const e164Digits = parsed.number.replace('+', '');

    this.isOtpLoading = true;
    this.otpError = null;

    this.authService.requestLoginOtp(e164Digits).subscribe({
      next: () => {
        this.isOtpLoading = false;
        this.otpStep = 'otp';
        this.startCountdown();
      },
      error: (err) => {
        this.isOtpLoading = false;
        this.otpError = err.error?.message || 'Failed to send OTP. Please try again.';
      },
    });
  }

  public verifyOtp(): void {
    if (!this.otpFormGroup.valid) return;

    const rawPhone = this.phoneFormGroup.value.phoneNumber?.trim();
    const parsed = parsePhoneNumber(rawPhone, this.selectedCountryCode);
    const e164Digits = parsed.number.replace('+', '');
    const otpCode = this.otpFormGroup.value.otpCode;

    this.isOtpLoading = true;
    this.otpError = null;

    this.authService.loginWithOtp(e164Digits, otpCode);
    this.isOtpLoading = false;
  }

  public resendOtp(): void {
    if (this.otpCountdown > 0) return;
    this.otpFormGroup.reset();
    this.requestOtp();
  }

  public backToPhone(): void {
    this.otpStep = 'phone';
    this.otpError = null;
    this.otpFormGroup.reset();
    this.countdownSub?.unsubscribe();
    this.otpCountdown = 0;
  }

  private startCountdown(): void {
    this.otpCountdown = 60;
    this.countdownSub?.unsubscribe();
    this.countdownSub = interval(1000)
      .pipe(take(60))
      .subscribe(() => {
        this.otpCountdown--;
      });
  }
}
